import { getSafeExternalUrl } from '@/libs/utils/safeExternalUrl';
import {
  PinakesStatus,
  type TPinakesAlsoOn,
  type TPinakesCard,
  type TPinakesResourceDetail,
} from '@/services/pinakes/pinakes.types';
import { ResourceFamily } from './ResourceCard.types';

/**
 * schema.org types whose cards carry extra details, nearest ancestor wins (see `getResourceFamily`).
 * A family is a group of types, never one type: every type below `Event` or `Place` in a card's
 * `type_path` gets that family's details, whatever Pinakes adds later.
 */
const FAMILY_ANCESTORS: Readonly<Record<string, ResourceFamily>> = {
  Event: ResourceFamily.EVENT,
  Place: ResourceFamily.PLACE,
};

/** A schema.org media type ends in `Object` (`VideoObject`, `ImageObject`); the label drops it. */
const MEDIA_OBJECT_SUFFIX = /(?<=.)Object$/;

/** Splits CamelCase into words and keeps acronyms whole: `FAQPage` → `FAQ Page`, `TVSeries` → `TV Series`. */
const CAMEL_CASE_BOUNDARY = /(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g;

/**
 * The card's type in plain words, from its schema.org `type` (else the deepest `type_path` entry):
 * `MusicRecording` → `Music recording`, `VideoObject` → `Video`, `FAQPage` → `FAQ page`.
 * `null` when the card has no type.
 */
export function getResourceTypeLabel(card: Pick<TPinakesCard, 'type' | 'type_path'>): string | null {
  const type = card.type || card.type_path.at(-1);
  if (!type) return null;

  const words = type.replace(MEDIA_OBJECT_SUFFIX, '').split(CAMEL_CASE_BOUNDARY);
  return words
    .map((word, index) => {
      // An acronym (`FAQ`, `TV`) keeps its case; every other word after the first is lower case.
      if (index === 0 || /^[A-Z0-9]+$/.test(word)) return word;
      return word.toLowerCase();
    })
    .join(' ');
}

/**
 * Which details a card shows, from the nearest ancestor in its `type_path` that has a family
 * (`Restaurant` → `LocalBusiness` → `Place`). Types with no such ancestor are plain web cards.
 */
export function getResourceFamily(card: Pick<TPinakesCard, 'type' | 'type_path'>): ResourceFamily {
  const path = card.type && card.type_path.at(-1) !== card.type ? [...card.type_path, card.type] : card.type_path;
  for (let index = path.length - 1; index >= 0; index--) {
    const family = FAMILY_ANCESTORS[path[index]];
    if (family) return family;
  }
  return ResourceFamily.WEB;
}

/** The card's `status` as one the card knows how to show: an unknown value reads as `error`. */
export function getResourceStatus(status: string): PinakesStatus {
  const known = Object.values(PinakesStatus).find((value) => value === status);
  return known ?? PinakesStatus.ERROR;
}

/**
 * `value` when it is an `http:` or `https:` URL, else `null`. Only these may become an `href` or an
 * image `src`: Pinakes URIs can be `isbn:`, `doi:`, `pubky://`, and a stored value could be anything.
 */
export function getHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const url = getSafeExternalUrl(value);
  if (!url) return null;
  const { protocol } = new URL(url);
  return protocol === 'http:' || protocol === 'https:' ? url : null;
}

/** A link without its scheme and `www.`, for a card that has no name: `https://www.x.com/a` → `x.com/a`. */
export function getDisplayLink(value: string): string {
  return value.replace(/^[a-z][a-z0-9+.-]*:(\/\/)?/i, '').replace(/^www\./i, '');
}

/** The host of an `http(s)` link without `www.`, else `null`. */
export function getLinkHost(value: string | null | undefined): string | null {
  const url = getHttpUrl(value);
  return url ? new URL(url).hostname.replace(/^www\./i, '') : null;
}

/**
 * `https://<host>/favicon.ico` for an `http(s)` link, else `null` (non-web URIs have no site to ask).
 * Always `https:`: the app is served over `https:`, where an `http:` image is mixed content.
 */
export function getFaviconUrl(value: string | null | undefined): string | null {
  const url = getHttpUrl(value);
  return url ? `https://${new URL(url).hostname}/favicon.ico` : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The `event_status` of an event going ahead as planned, as stable sends it. */
const SCHEDULED_EVENT_STATUS = 'scheduled';

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function createDateFormat(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    // An unknown IANA zone is a RangeError: show the time in the viewer's own zone instead.
    const inViewerZone = { ...options };
    delete inViewerZone.timeZone;
    delete inViewerZone.timeZoneName;
    return new Intl.DateTimeFormat(locale, inViewerZone);
  }
}

/**
 * When an event happens, in the viewer's locale: in the event's time zone with its name when the
 * card has `tz`, else in the viewer's zone. All-day events show dates only, read in UTC with `end`
 * exclusive, as stable's calendar occurrences encode them (`00:00Z` → next day's `00:00Z`), so a
 * one-day event shows its date alone. `null` without a valid `start`.
 *
 * @param locale - For tests; the viewer's locale when omitted
 */
export function formatResourceDates(
  card: Pick<TPinakesResourceDetail, 'start' | 'end' | 'tz' | 'all_day'>,
  locale?: string,
): string | null {
  const start = parseDate(card.start);
  if (!start) return null;
  const end = parseDate(card.end);

  if (card.all_day) {
    const format = createDateFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' });
    const lastDay = end ? new Date(end.getTime() - DAY_MS) : null;
    if (!lastDay || lastDay.getTime() <= start.getTime()) return format.format(start);
    return format.formatRange(start, lastDay);
  }

  const format = createDateFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(card.tz ? { timeZone: card.tz, timeZoneName: 'short' } : {}),
  });
  if (!end || end.getTime() <= start.getTime()) return format.format(start);
  return format.formatRange(start, end);
}

/**
 * The note for an event that is not going ahead as planned: its `event_status` as written. `null`
 * for `scheduled` or no status.
 */
export function getEventStatusNote(eventStatus: string | null | undefined): string | null {
  const value = eventStatus?.trim();
  if (!value || value.toLowerCase() === SCHEDULED_EVENT_STATUS) return null;
  return `Event status: ${value}`;
}

/** A point as `lat, lon` in degrees, four decimals (about 10 m). */
export function formatResourcePoint(point: { lat: number; lon: number }): string {
  return `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`;
}

/** The `kind` of a derived link that is an archived copy; its `at` is the capture. */
const ARCHIVED_KIND = 'archived';

/** A documented `kind` whose plain wording is not its value with a capital: an acronym. */
const KIND_LABELS: Readonly<Record<string, string>> = { pdf: 'PDF' };

/** The name of an Also on entry: its service, else the link's host, else the link as written. */
export function getAlsoOnName(entry: Pick<TPinakesAlsoOn, 'uri' | 'site'>): string {
  return entry.site?.trim() || getLinkHost(entry.uri) || getDisplayLink(entry.uri);
}

/**
 * What an Also on entry says under its name: the identifier of a URI that is not a web link and is
 * named by its service (`doi:10.1038/nature12373` named "DOI"), an archived copy with its capture
 * date, another qualifier (`kind`), and for a derived link (nobody tagged it) the source it was
 * found through. `null` for a tagged web link without a qualifier.
 *
 * The capture date is read in UTC, the zone archives such as the Wayback Machine date captures in.
 *
 * @param locale - For tests; the viewer's locale when omitted
 */
export function getAlsoOnNote(
  entry: Pick<TPinakesAlsoOn, 'uri' | 'site' | 'derived' | 'kind' | 'via' | 'at'>,
  locale?: string,
): string | null {
  const parts: string[] = [];
  const kind = entry.kind?.trim();
  if (kind?.toLowerCase() === ARCHIVED_KIND) {
    const at = parseDate(entry.at);
    parts.push(
      at ? `Archived ${createDateFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(at)}` : 'Archived',
    );
  } else if (kind) {
    parts.push(KIND_LABELS[kind.toLowerCase()] ?? kind);
  }
  if (entry.derived) {
    const via = entry.via?.trim();
    parts.push(via ? `found via ${via}` : 'found automatically');
  }
  const qualifier = parts.length > 0 ? parts.join(' · ') : null;
  const note = qualifier && qualifier.charAt(0).toUpperCase() + qualifier.slice(1);

  // A non-web entry has no link to open: without its identifier, a row named "DOI" says nothing.
  // Kept as written (no capital): an identifier's case can matter.
  const identifier = getHttpUrl(entry.uri) ? null : getDisplayLink(entry.uri);
  if (identifier && identifier !== getAlsoOnName(entry)) {
    return note ? `${identifier} · ${note}` : identifier;
  }
  return note;
}

/**
 * Where a card from a list (Similar) opens: its own link when that is web, else the address a Pubky
 * app gives for it (`open_in`), else its first web link on another service (`also_on`), so an
 * `isbn:` book still opens somewhere. A `missing` card's own link is gone upstream, so it opens
 * elsewhere. `null` when it has no web link at all (a `geo:` point).
 */
export function getResourceLink(card: Pick<TPinakesCard, 'uri' | 'status' | 'open_in' | 'also_on'>): string | null {
  const own = getResourceStatus(card.status) === PinakesStatus.MISSING ? [] : [card.uri];
  const candidates = [...own, ...card.open_in.map((entry) => entry.url), ...card.also_on.map((entry) => entry.uri)];
  for (const candidate of candidates) {
    const url = getHttpUrl(candidate);
    if (url) return url;
  }
  return null;
}

/**
 * The note under a card in a list (Similar): its type in plain words and its site, else the host of
 * the link the row opens. A row that opens another service than the card's own link names that
 * service's host, not the card's site. `null` when it has neither.
 */
export function getResourceListNote(
  card: Pick<TPinakesCard, 'uri' | 'type' | 'type_path' | 'site'>,
  href: string | null,
): string | null {
  const site = !href || href === getHttpUrl(card.uri) ? card.site?.trim() : null;
  const parts = [getResourceTypeLabel(card), site || getLinkHost(href)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
